import { IOptionAnyValue } from 'bricks/types';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';
import { isAllApartmentModels, isStudentHousingModel } from 'helpers/proForma';
import {
    IDispositionData, IOperatingInfo, IProFormaGeneralInfo, SaleProceeds,
} from '../../../types';
import { ProFormaGenericRow } from '../../ProFormaGenericTable/types';
import {
    APARTMENT_UNIT_TYPE_NAME, UnitTypeOptionsApartment,
    UnitTypeOptionsNonApartmentEstimate, UnitTypeOptionsNonApartmentTenants,
    NON_APARTMENT_UNIT_TYPE_NAME, UnitTypeOptionsDataCenter,
    STUDENT_HOUSING_UNIT_TYPE_NAME, UnitTypeOptionsStudentHousing,
} from './constants';

export const getColumnTitle = (key: string): string => {
    switch (key) {
        case 'untrended':
            return 'Untrended';
        case 'trended':
            return 'Trended';
        case 'sale':
            return 'Sale';
        default:
            return '';
    }
};

export const getDispositionPeriodSubtitle = (key: string, disposition?: IDispositionData): string => {
    if (!disposition?.trendedPeriod?.start || !disposition?.salePeriod?.start) return '';

    switch (key) {
        case 'untrended':
            return '(Today)';
        case 'trended':
            return `(Period ${disposition?.trendedPeriod?.start} - ${disposition?.trendedPeriod?.end})`;
        case 'sale':
            return `(Period ${disposition?.salePeriod?.start} - ${disposition?.salePeriod?.end})`;
        default:
            return '';
    }
};

export const getSaleProceedsData = (disposition: IDispositionData | undefined, key: string): SaleProceeds | undefined => {
    const defaultValues = {
        amount: 0,
        perUnit: 0,
        perSf: 0,
        perGrossSf: 0,
        perBed: 0,
        perKw: 0,
        perMw: 0,
    };
    if (!disposition) {
        return defaultValues;
    }
    const dispositionData = disposition as Record<string, any>;
    const data = dispositionData[key] as SaleProceeds;
    if (data && data.perUnit === null && data.perSf === null && data.amount === null && data.perGrossSf === null
        && (data.perBed === null || data.perBed === undefined)) {
        return defaultValues;
    }
    return data;
};

export const createRow = (id: string, title: string, data: SaleProceeds | undefined | null, selectedType: string): ProFormaGenericRow => {
    const baseRow = {
        id,
        title,
        amount: data?.amount !== undefined && data?.amount !== null ? String(data.amount) : '0',
        isReadOnly: true,
    };

    let value = data?.perSf;
    if (selectedType === APARTMENT_UNIT_TYPE_NAME.UNIT) {
        value = data?.perUnit;
    } else if (selectedType === STUDENT_HOUSING_UNIT_TYPE_NAME.BED) {
        value = data?.perBed;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.GSF) {
        value = data?.perGrossSf;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.KW) {
        value = data?.perKw;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.MW) {
        value = data?.perMw;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.RACK) {
        value = data?.perRack;
    }

    return {
        ...baseRow,
        valueByType: value !== undefined && value !== null ? String(value) : '0',
    };
};

export const getUnitTypeOptions = (
    operatingInfo: IOperatingInfo,
    proForma: IProFormaGeneralInfo,
) => {
    if (isStudentHousingModel(proForma?.proFormaModelId)) {
        return UnitTypeOptionsStudentHousing;
    }
    if (isAllApartmentModels(proForma?.proFormaModelId)) {
        return UnitTypeOptionsApartment;
    }

    let nonApartmentTypeOptions: IOptionAnyValue[] = [];
    if (operatingInfo.incomeCalculationType === IncomeCalculationType.ESTIMATE) {
        nonApartmentTypeOptions = UnitTypeOptionsNonApartmentEstimate;
    }
    if (operatingInfo.incomeCalculationType === IncomeCalculationType.TENANTS) {
        nonApartmentTypeOptions = UnitTypeOptionsNonApartmentTenants;
    }
    if (proForma?.proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        nonApartmentTypeOptions = [
            ...nonApartmentTypeOptions,
            ...UnitTypeOptionsDataCenter,
        ];
    }

    return nonApartmentTypeOptions;
};
